import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';
import { UPDATE_FIELD } from '../../../store/modules/state/formBuilder';
import { FormBuilderFieldFormContext } from '../FormBuilderFieldForm';
import ScoringPanelView from '../view/ScoringPanelView';
import { FormBuilderEditContext } from '../FormBuilderEdit';
import ScoringPanelMultiView from '../view/ScoringPanelMultiView';

const ScoringPanelReducer = (state, action) => {
  const { type, payload = [] } = action;

  switch (type) {
    case 'reload':
      return [...payload];
    default:
      return state;
  }
};

const ScoringPanelMultiReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'reload':
      return [...payload];
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const ScoringPanelEngine = ({
  fieldOptions: givenFieldOptions = [],
  autosaveField,
  autosaveSubfield
}) => {
  const { field: mainField } = useContext(FormBuilderFieldFormContext);
  const { allFieldsWithSubFields } = useContext(FormBuilderEditContext);
  let field = mainField;
  let fieldOptions = givenFieldOptions;
  let countrySubfield;
  let subFieldExternalIdsToScore = [];
  let scoringSubfields = [];
  let scoringSubfieldsById = {};
  let scoringSubfieldInternalIds = [];
  let subFieldOptionsMap = {};
  let isScoringOnSubfields = false;

  if (
    mainField.uiType === 'worldcheck' ||
    mainField.uiType === 'worldcheck_org'
  ) {
    isScoringOnSubfields = true;
    if (mainField.uiType === 'worldcheck') {
      subFieldExternalIdsToScore = [
        'countryLocation',
        'placeOfBirth',
        'nationality'
      ];
    } else {
      subFieldExternalIdsToScore = ['registeredCountry'];
    }
    scoringSubfields = allFieldsWithSubFields.filter(one => {
      return (
        subFieldExternalIdsToScore.includes(
          one?.lookupInfo?.subFieldInfo?.externalFormFieldId
        ) &&
        one?.lookupInfo?.subFieldInfo?.parentFieldId === mainField.internalId
      );
    });
    scoringSubfields.forEach(subField => {
      scoringSubfieldInternalIds.push(subField.internalId);
      subFieldOptionsMap[subField.internalId] = subField.options;
      scoringSubfieldsById[subField.internalId] = subField;
    });
  }

  if (mainField.uiType === 'credas') {
    countrySubfield = allFieldsWithSubFields.find(one => {
      return (
        one?.lookupInfo?.subFieldInfo?.externalFormFieldId === 'country' &&
        one?.lookupInfo?.subFieldInfo?.parentFieldId === mainField.internalId
      );
    });
    if (countrySubfield) {
      field = countrySubfield;
      fieldOptions = field.options;
    }
  }
  const initialState = fieldOptions.map(one => {
    return { optionName: one, value: 0 };
  });
  const [data, dispatch] = useReducer(ScoringPanelReducer, initialState);
  const firstSubField =
    scoringSubfields && scoringSubfields.length ? scoringSubfields[0] : null;
  let initialSubfieldExpandedId = firstSubField?.internalId || null;
  let multiInitialState = {
    subfieldExpandedId: initialSubfieldExpandedId,
    filteredSubfieldOptions: firstSubField?.options || [],
    filterText: ''
  };
  scoringSubfields.forEach(subField => {
    multiInitialState[subField.internalId] = subField.options.map(one => {
      return { optionName: one, value: 0 };
    });
  });
  const [subFieldData, dispatchSubField] = useReducer(
    ScoringPanelMultiReducer,
    multiInitialState
  );

  useEffect(() => {
    const calculationMapping =
      field.calculationMapping && field.calculationMapping.length
        ? field.calculationMapping
        : initialState;

    const subfieldCalculationMapping = {};
    scoringSubfields.forEach(subField => {
      subfieldCalculationMapping[subField.internalId] =
        subField.calculationMapping && subField.calculationMapping.length
          ? subField.calculationMapping
          : multiInitialState[subField.internalId];
    });
    subfieldCalculationMapping.filteredSubfieldOptions =
      scoringSubfields && scoringSubfields[0] && scoringSubfields[0].options
        ? scoringSubfields[0].options
        : [];
    dispatch({
      type: 'reload',
      payload: calculationMapping
    });
    dispatchSubField({
      type: 'update',
      payload: subfieldCalculationMapping
    });
  }, []);

  const onScoringValuesSaved = useCallback(
    (event, option) => {
      const newState = data.map(obj =>
        obj.optionName === option
          ? {
              ...obj,
              value: event.target.value ? parseInt(event.target.value) : ''
            }
          : { ...obj }
      );
      dispatch({ type: 'reload', payload: newState });
    },
    [data]
  );

  const onScoringValuesBlur = useCallback(() => {
    data.forEach(element => {
      if (!element.value) {
        element.value = 0;
      }
    });

    if (mainField.uiType === 'credas') {
      autosaveSubfield(UPDATE_FIELD, {
        fieldId: countrySubfield.internalId,
        field: {
          calculationMapping: data
        }
      });
    } else {
      autosaveField(UPDATE_FIELD, {
        field: {
          calculationMapping: data
        }
      });
    }
  }, [data]);

  const onSubFieldScoringValuesSaved = useCallback(
    (event, subfieldId, option) => {
      const newState = subFieldData[subfieldId].map(obj =>
        obj.optionName === option
          ? {
              ...obj,
              value: event.target.value ? parseInt(event.target.value) : ''
            }
          : { ...obj }
      );
      dispatchSubField({
        type: 'update',
        payload: {
          [subfieldId]: newState
        }
      });
    },
    [subFieldData]
  );

  const onSubFieldScoringValuesBlur = useCallback(
    subfieldId => {
      autosaveSubfield(UPDATE_FIELD, {
        fieldId: subfieldId,
        field: {
          calculationMapping: subFieldData[subfieldId]
        }
      });
    },
    [subFieldData]
  );

  const onExpandSubField = subfieldId => {
    if (subFieldData.subfieldExpandedId === subfieldId) {
      dispatchSubField({
        type: 'update',
        payload: {
          filterText: '',
          subfieldExpandedId: null
        }
      });
    } else {
      dispatchSubField({
        type: 'update',
        payload: {
          subfieldExpandedId: subfieldId,
          filterText: '',
          filteredSubfieldOptions:
            scoringSubfieldsById[subfieldId]?.options || []
        }
      });
    }
  };

  const onFilterTextChanged = event => {
    let val = event.target.value || '';
    dispatchSubField({
      type: 'update',
      payload: {
        filterText: val,
        filteredSubfieldOptions: (
          scoringSubfieldsById[subFieldData.subfieldExpandedId]?.options || []
        ).filter(option => option.toLowerCase().includes(val.toLowerCase()))
      }
    });
  };

  const viewProps = {
    fieldOptions,
    field,
    data,
    onScoringValuesSaved,
    onScoringValuesBlur,
    isScoringOnSubfields,
    scoringSubfields,
    scoringSubfieldsById,
    scoringSubfieldInternalIds,
    subFieldOptionsMap,
    subFieldData,
    onFilterTextChanged,
    onSubFieldScoringValuesSaved,
    onSubFieldScoringValuesBlur,
    onExpandSubField
  };

  if (isScoringOnSubfields) {
    return <ScoringPanelMultiView {...viewProps} />;
  }

  return <ScoringPanelView {...viewProps} />;
};

export default ScoringPanelEngine;
